'use client';

import React, { useState, useEffect, useRef } from 'react';
import { Volume2, Plus, Music, Loader2, Trash2 } from 'lucide-react';
import { SOUNDS } from '@/lib/constants';

type AudioFile = {
  name: string;
  url: string;
};

// --- COLORS FOR DYNAMIC BUTTONS ---
const BUTTON_COLORS = [
  'bg-red-600', 'bg-blue-600', 'bg-green-600', 'bg-purple-600', 
  'bg-orange-600', 'bg-pink-600', 'bg-amber-600', 'bg-emerald-600',
  'bg-indigo-600', 'bg-cyan-600'
];

export default function SoundBoard() {
  const lastPlayedId = useRef<number>(0);
  const [customSounds, setCustomSounds] = useState<AudioFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // --- AUDIO LOGIC ---
  const playAudioUrl = (url: string) => {
    const audio = new Audio(url);
    audio.volume = 0.7;
    audio.play().catch(e => console.error("Autoplay blocked (click first!):", e));
  };

  const fetchCustomSounds = async () => {
    try {
      const res = await fetch('/api/files?type=audio');
      const audioFiles = await res.json();
      setCustomSounds(audioFiles);
    } catch (e) {
      console.error("Failed to load custom sounds");
    }
  };

  const playTTS = async (text: string) => {
    try {
      const response = await fetch('/api/tts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text }),
      });

      if (!response.ok) throw new Error('API failed');

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.play();
    } catch (error) {
      console.warn("TTS Fallback:", error);
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'de-DE';
      window.speechSynthesis.speak(utterance);
    }
  };

  // --- SYNC LOGIC (POLLING) ---
  useEffect(() => {
    fetchCustomSounds();
    const interval = setInterval(async () => {
      try {
        const res = await fetch('/api/events');
        const event = await res.json();

        if (event.id && event.id > lastPlayedId.current) {
          const age = Math.abs(Date.now() - event.id); // Math.abs falls Client-Uhr NACH geht
          if (age < 60000) { // 60 Sekunden Puffer
             if (event.type === 'sound') {
               // Robust URL construction
               let soundUrl = event.payload;
               if (!soundUrl.startsWith('/')) {
                 soundUrl = `/audio/${soundUrl}`;
               }
               // Add a timestamp to bypass aggressive proxy caching if needed
               const finalUrl = `${soundUrl}?t=${event.id}`;
               playAudioUrl(finalUrl);
             } else if (event.type === 'tts') {
               playTTS(event.payload);
             }
          }
          lastPlayedId.current = event.id;
        }
      } catch (e) {
        // Silent error
      }
    }, 500);

    return () => clearInterval(interval);
  }, []);

  // --- ACTIONS ---
  const broadcastSound = async (fileName: string) => {
    // We send the filename now, and build the URL on playback
    await fetch('/api/events', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'sound', payload: fileName }),
    });
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) return;
    const file = e.target.files[0];
    
    // Prompt for custom name
    const customName = prompt("Wie soll der Sound heißen?", file.name.replace(/\.[^/.]+$/, ""));
    if (!customName) return; // Cancelled

    setIsUploading(true);
    const formData = new FormData();
    
    // Wir müssen die Datei umbenennen, bevor wir sie senden
    // (Da File-Objekte read-only sind, erstellen wir ein neues)
    const fileExtension = file.name.split('.').pop();
    const newFileName = `${customName}.${fileExtension}`;
    const renamedFile = new File([file], newFileName, { type: file.type });

    formData.append('file', renamedFile);

    try {
      const res = await fetch('/api/files', { method: 'POST', body: formData });
      if (res.ok) {
        await fetchCustomSounds();
      }
    } catch (e) {
      alert("Upload fehlgeschlagen");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const deleteSound = async (fileName: string) => {
    if (!confirm(`"${fileName}" wirklich löschen?`)) return;
    try {
      await fetch('/api/files', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileName })
      });
      fetchCustomSounds();
    } catch (e) {
      console.error("Delete failed");
    }
  };

  return (
    <div className="bg-slate-900 border border-slate-700 rounded-xl p-4 shadow-xl relative overflow-hidden">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Volume2 className="text-purple-500 w-5 h-5" />
          <h2 className="text-sm font-black text-white uppercase tracking-wider">Soundboard</h2>
        </div>
        
        <label className="cursor-pointer bg-slate-800 hover:bg-slate-700 p-1.5 rounded-lg transition-colors group">
          {isUploading ? <Loader2 className="w-4 h-4 text-purple-400 animate-spin" /> : <Plus className="w-4 h-4 text-slate-400 group-hover:text-white" />}
          <input type="file" ref={fileInputRef} onChange={handleFileUpload} accept="audio/*" className="hidden" />
        </label>
      </div>

      <div className="overflow-y-auto pr-1 max-h-[500px] custom-scrollbar">
        {customSounds.length === 0 ? (
          <div className="h-32 flex flex-col items-center justify-center text-slate-600 space-y-2 opacity-50 border-2 border-dashed border-slate-800 rounded">
            <Music className="w-8 h-8" />
            <p className="text-xs font-medium italic">Leer...</p>
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-2">
            {customSounds.map((sound, index) => {
              const colorClass = BUTTON_COLORS[index % BUTTON_COLORS.length];
              return (
                <div key={sound.name} className="relative group">
                  <button
                    onClick={() => broadcastSound(sound.name)}
                    className={`${colorClass} w-full hover:brightness-110 active:scale-95 text-white font-bold h-12 rounded shadow-md transition-all flex flex-col items-center justify-center overflow-hidden px-1`}
                  >
                    <span className="truncate w-full text-xs leading-tight text-center drop-shadow-md">
                      {sound.name.replace(/\.[^/.]+$/, "").replace(/_/g, " ")}
                    </span>
                  </button>
                  <button 
                    onClick={() => deleteSound(sound.name)}
                    className="absolute -top-1 -right-1 bg-black/80 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow-xl border border-slate-700 hover:bg-red-600 z-10"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
